/**
 * BudgetModal — public-facing summary of BOM (the closest analog to a
 * Mini-Project budget in v1).
 *
 * DEV-1080, Chunk B. Opens when any viewer clicks the Budget shape on
 * the Mini-Project Progress tab. Status doesn't currently carry budget
 * fields — Skip's call on 2026-05-22 is to surface BOM as a stand-in
 * until we lock down what a Mini-Project budget should mean. The KPI
 * shape itself stays grey (per computeKpiStatus.ts §"Budget — TBD") so
 * we don't lie about a color we can't yet defend.
 *
 * Composition:
 *   - Header: "Budget" + close ×
 *   - Counts row: obtained / needed / N-A
 *   - Total estimated cost, grouped by currency (only shown if any item
 *     carries cost data — most BOMs won't initially)
 *   - Item list: name, state pill, quantity × unit, cost (if set)
 *   - Footer hint: "Edit BOM from the Progress tab" (Lead-only) and a
 *     soft note that KPI color is currently grey by design.
 *
 * Strings localized via `pages:miniProject.kpiModals.budget.*`
 * (Chunk D Phase 4 sweep, 2026-05-22).
 */

import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProjectBomItem } from "../../api/miniProjects";
import styles from "./BudgetModal.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  bomItems: MiniProjectBomItem[];
  isLead: boolean;
}

/* ── Component ─────────────────────────────────────────────────── */

const BudgetModal: React.FC<Props> = ({ open, onClose, bomItems, isLead }) => {
  const { t } = useAppTranslation("pages");
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  const counts = useMemo(() => {
    let needed = 0, obtained = 0, na = 0;
    for (const i of bomItems) {
      if (i.procurementState === "obtained") obtained++;
      else if (i.procurementState === "needed") needed++;
      else na++;
    }
    return { needed, obtained, na, total: bomItems.length };
  }, [bomItems]);

  // Per-currency totals. Only sums items where both cost AND quantity
  // are non-null — otherwise we'd be inventing data. Items with cost
  // but no quantity default quantity to 1.
  const costByCurrency = useMemo(() => {
    const m = new Map<string, { obtained: number; needed: number }>();
    for (const i of bomItems) {
      if (i.estimatedCost == null) continue;
      const qty = i.quantity ?? 1;
      const subtotal = i.estimatedCost * qty;
      const ccy = (i.currencyCode || "USD").toUpperCase();
      const slot = m.get(ccy) || { obtained: 0, needed: 0 };
      if (i.procurementState === "obtained") slot.obtained += subtotal;
      else if (i.procurementState === "needed") slot.needed += subtotal;
      m.set(ccy, slot);
    }
    return m;
  }, [bomItems]);

  const hasAnyCost = costByCurrency.size > 0;

  // Items sorted: needed first, then obtained, then N/A — so the things
  // that matter operationally bubble to the top.
  const sorted = useMemo(() => {
    const stateRank: Record<MiniProjectBomItem["procurementState"], number> = {
      needed: 0,
      obtained: 1,
      not_applicable: 2,
    };
    return [...bomItems].sort((a, b) => {
      const sr = stateRank[a.procurementState] - stateRank[b.procurementState];
      if (sr !== 0) return sr;
      return a.position - b.position;
    });
  }, [bomItems]);

  if (!open) return null;

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={t("miniProject.kpiModals.budget.title")}>
        <header className={styles.header}>
          <span className={styles.title}>{t("miniProject.kpiModals.budget.title")}</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("miniProject.kpiModals.budget.close")}>
            ×
          </button>
        </header>

        <div className={styles.body}>
          {bomItems.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>{t("miniProject.kpiModals.budget.empty.title")}</p>
              <p className={styles.emptyHint}>
                {isLead
                  ? t("miniProject.kpiModals.budget.empty.leadHint")
                  : t("miniProject.kpiModals.budget.empty.publicHint")}
              </p>
            </div>
          ) : (
            <>
              {/* Counts row */}
              <div className={styles.statRow}>
                <Stat label={t("miniProject.kpiModals.budget.stat.needed")} value={counts.needed} variant="warning" />
                <Stat label={t("miniProject.kpiModals.budget.stat.obtained")} value={counts.obtained} variant="success" />
                {counts.na > 0 && <Stat label={t("miniProject.kpiModals.budget.stat.notApplicable")} value={counts.na} variant="muted" />}
                <Stat label={t("miniProject.kpiModals.budget.stat.totalItems")} value={counts.total} variant="default" />
              </div>

              {/* Cost rollup (only if any item has cost data) */}
              {hasAnyCost && (
                <div className={styles.costRow}>
                  <div className={styles.sectionTitle}>{t("miniProject.kpiModals.budget.cost.title")}</div>
                  <table className={styles.costTable}>
                    <thead>
                      <tr>
                        <th>{t("miniProject.kpiModals.budget.cost.currency")}</th>
                        <th className={styles.numCell}>{t("miniProject.kpiModals.budget.cost.obtained")}</th>
                        <th className={styles.numCell}>{t("miniProject.kpiModals.budget.cost.stillNeeded")}</th>
                        <th className={styles.numCell}>{t("miniProject.kpiModals.budget.cost.total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(costByCurrency.entries())
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([ccy, v]) => (
                          <tr key={ccy}>
                            <td>{ccy}</td>
                            <td className={styles.numCell}>{formatMoney(v.obtained, ccy)}</td>
                            <td className={styles.numCell}>{formatMoney(v.needed, ccy)}</td>
                            <td className={styles.numCell}>
                              {formatMoney(v.obtained + v.needed, ccy)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Item list */}
              <div className={styles.tableWrap}>
                <div className={styles.sectionTitle}>{t("miniProject.kpiModals.budget.items.title")}</div>
                <table className={styles.itemTable}>
                  <thead>
                    <tr>
                      <th>{t("miniProject.kpiModals.budget.items.item")}</th>
                      <th>{t("miniProject.kpiModals.budget.items.state")}</th>
                      <th className={styles.numCell}>{t("miniProject.kpiModals.budget.items.qty")}</th>
                      <th className={styles.numCell}>{t("miniProject.kpiModals.budget.items.estCost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((i) => (
                      <tr key={i.bomItemGuid}>
                        <td>
                          <div className={styles.itemName}>{i.name}</div>
                          {i.notes && <div className={styles.itemNotes}>{i.notes}</div>}
                        </td>
                        <td>
                          <span
                            className={`${styles.statePill} ${styles[`state_${i.procurementState}`]}`}
                          >
                            {i.procurementState === "not_applicable"
                              ? t("miniProject.kpiModals.budget.state.notApplicableShort")
                              : i.procurementState === "obtained"
                                ? t("miniProject.kpiModals.budget.state.obtained")
                                : t("miniProject.kpiModals.budget.state.needed")}
                          </span>
                        </td>
                        <td className={styles.numCell}>
                          {i.quantity != null
                            ? `${i.quantity}${i.unit ? ` ${i.unit}` : ""}`
                            : "—"}
                        </td>
                        <td className={styles.numCell}>
                          {i.estimatedCost != null
                            ? formatMoney(
                                i.estimatedCost * (i.quantity ?? 1),
                                i.currencyCode || "USD",
                              )
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Honest disclaimer */}
              <div className={styles.greyNote}>
                <strong>{t("miniProject.kpiModals.budget.greyNote.label")}</strong>{" "}
                {t("miniProject.kpiModals.budget.greyNote.body")}
              </div>

              {isLead && (
                <div className={styles.leadHint}>
                  {t("miniProject.kpiModals.budget.leadHint")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default BudgetModal;

/* ── Helpers ───────────────────────────────────────────────────── */

function Stat({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant: "warning" | "success" | "muted" | "default";
}) {
  return (
    <div className={`${styles.stat} ${styles[`stat_${variant}`]}`}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(0)} ${currency}`;
  }
}
